import { Alert, ControlLabel, Dialog, Radio, RadioGroup, Skeleton, Stack } from '@all-manager/design-system-v2';
import { Controller } from "react-hook-form";
import ContactNameAutocomplete from "../../../../../components/ContactNameAutocomplete/ContactNameAutocomplete";
import DateField from "../../../../../components/modals/components/DateFieldOk";
import DescriptionFieldOk from "../../../../../components/modals/components/DescriptionFieldOk";
import ProviderAutocomplete from "../../../../../components/ProviderAutocomplete/ProviderAutocomplete";
import type { SubjectHistoryFeed } from "../../../../../types";
import DocumentsFormSection from "../../../../documents/external-components/DocumentsFormSection/DocumentsFormSection";
import RevealFieldButton from "../RevealFieldButton/RevealFieldButton";
import useAddCallActionModal from "./useAddCallActionModal";

type Props = {
  onClose: () => void;
  subjectId: string;
  item?: SubjectHistoryFeed;
}

export default function AddCallActionModal(props: Props) {
  const {
    form,
    handleConfirm,
    isLoading,
    serverError,
    isDocumentLoading,
    showDocuments,
    revealDocuments,
  } = useAddCallActionModal(props);

  return (
    <Dialog
      open
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      title={props.item ? "Modifier la communication" : "Nouvelle communication"}
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: 'Enregistrer',
          onClick: form.handleSubmit(handleConfirm),
          loading: isLoading,
          disabled: isDocumentLoading,
        },
        secondaryAction: {
          label: 'Annuler',
          onClick: props.onClose,
          disabled: isLoading,
        },
      }}
    >
      <Stack spacing={2}>
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}

        <Controller
          control={form.control}
          name='communication_type'
          render={({ field, fieldState }) => (
            <RadioGroup
              row
              label="Type d'échange"
              name="communication_type"
              value={field.value}
              onChange={(event) => field.onChange(event.target.value)}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            >
              <ControlLabel value="email" control={<Radio />} label="E-mail" />
              <ControlLabel value="call" control={<Radio />} label="Appel" />
            </RadioGroup>
          )}
        />

        <DateField
          name="date"
          form={form}
          label="Date"
          isTimeManaged={false}
          required
        />

        <Controller
          control={form.control}
          name='contact_name'
          render={({ field, fieldState }) => (
            <ContactNameAutocomplete
              label="Expéditeur"
              value={field.value ?? ''}
              onChange={field.onChange}
              fullWidth
              required
              autoFocus
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={form.control}
          name='provider_name'
          render={({ field, fieldState }) => (
            <ProviderAutocomplete
              label="Destinataire"
              value={field.value ?? ''}
              onChange={field.onChange}
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <DescriptionFieldOk
          form={form}
          name="comment"
          label="Commentaire"
        />

        {showDocuments ? (
          isDocumentLoading ? (
            <Skeleton variant="rounded" height={120} aria-label="Chargement des documents" />
          ) : (
            <Controller
              name='documents'
              control={form.control}
              render={({ field }) => (
                <DocumentsFormSection
                  documents={field.value}
                  onChange={field.onChange}
                  subjectId={props.subjectId}
                />
              )}
            />
          )
        ) : (
          <RevealFieldButton label="Ajouter des documents" onClick={revealDocuments} />
        )}
      </Stack>
    </Dialog>
  );
}
